import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = `${API_BASE_PATH}`;

// ✅ Auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ✅ Error handler
const showAxiosError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail || error?.message || fallback;
  console.error("Axios Error:", error); // Full error object
  console.error("Message:", detail); // Friendly error message
};

// ✅ Branch Type
export interface Branch {
  branch_id?: string;
  branch_name?: string;
  branch_address: string;
  branch_city: string;
  branch_state: string;
  branch_zipcode: string;
  branch_country: string;
  gst_number: string;
}

// ✅ Contact type used in frontend
export interface Contact {
  contact_id: string;
  contact_name: string;
  contact_mail?: string;
  contact_phone?: string;
  contact_description?: string;
  contact_address?: string;
  contact_city?: string;
  contact_state?: string;
  contact_zipcode?: string;
  contact_country?: string;
  contact_Company_name?: string;
  contact_desigination?: string;
  gst_number?: string;
  website_url?: string;
  lead_from?: string;
  status?: string;
  created_at?: string;
  last_viewed?: string;
  address_mode?: "single" | "multiple";
  branches?: Branch[];
  client_id: string;
  leadsource_id?: string;
  leadsource_name?: string;
  username?: string; // ✅ Add this
  meta_page?: string;
  contact_notes?: string;
  users?: string; // ✅ Add this
  leadgivenby_id?: string;
  LeadGivenBy_name?: string;
  leadgivenby_name?: string;
}

// ✅ Payload for create/update company
export interface UpdateCompanyPayload {
  company_name?: string;
  company_mail?: string;
  website_url?: string;
  company_phone?: string;
  contact_remark?: string;
  contact_person_name?: string;
  contact_person_desigination?: string;
  leadsource_id?: string;
  leadgivenby_id?: string;
  branches?: {
    branch_address: string;
    branch_city: string;
    branch_state: string;
    branch_zipcode: string;
    branch_country: string;
    gst_number: string;
  }[];
}

// ✅ Raw format from backend
export interface RawCompany {
  contact_id: string;
  company_name: string;
  company_mail?: string;
  company_phone?: string;
  contact_remark?: string;
  contact_person_name: string;
  contact_person_desigination?: string;
  leadsource_id?: string;
  leadsource_name?: string;
  leadgivenby_id?: string;
  LeadGivenBy_name?: string;
  client_id: string;
  status?: string;
  website_url?: string; // ✅ Add this
  created_at?: string; // ✅ Add this
   leadowner_id?: string;   // ✅ Add this
  leadowner_name?: string; // ✅ Add this
  users?: string;
  meta_page: string;
  username?: string; // ✅ Add this
}

export interface CompanyResponse {
  company: RawCompany;
  branches: Branch[];
}

export interface ImportSummary {
  reference_name: string;
  total_imported: number;
  total_non_duplicates: number;
}

// ✅ Fetch all contacts
export const fetchAllContacts = async (): Promise<CompanyResponse[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/companies/`, {
      headers: getAuthHeaders(),
    });
    const companies = res.data?.companies;
    if (!Array.isArray(companies)) {
      throw new Error("API response 'companies' is not an array");
    }
    return companies;
  } catch (error) {
    showAxiosError(error, "Failed to fetch contacts");
    throw error;
  }
};

// ✅ Fetch contact by ID
export const fetchContactById = async (id: string): Promise<Contact> => {
  try {
    const res = await axios.get(`${BASE_URL}/Contacts/client/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch contact");
    throw error;
  }
};

// ✅ Create Contact
export const createContact = async (
  data: Partial<Contact>
): Promise<CompanyResponse> => {
  try {
    const formData = new URLSearchParams();
    for (const key in data) {
      const value = data[key as keyof Contact];
      if (value !== null && value !== undefined) {
        formData.append(key, value as string);
      }
    }

    const res = await axios.post(`${BASE_URL}/company/`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...getAuthHeaders(),
      },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

// ✅ Update Contact / Company
export const updateCompany = async (
  contact_id: string,
  data: UpdateCompanyPayload
): Promise<CompanyResponse> => {
  try {
    const form = new URLSearchParams();
    form.append("company_name", data.company_name || "");
    form.append("company_mail", data.company_mail || "");
    form.append("website_url", data.website_url || "");
    form.append("company_phone", data.company_phone || "");
    form.append("contact_remark", data.contact_remark || "");
    form.append("contact_person_name", data.contact_person_name || "");
    form.append(
      "contact_person_desigination",
      data.contact_person_desigination || ""
    );
    form.append("leadsource_id", data.leadsource_id || "");
    form.append("leadgivenby_id", data.leadgivenby_id || "");

    (data.branches || []).forEach((branch, index) => {
      const i = index + 1;
      form.append(`branch_name_${i}`, `Branch ${i}`);
      form.append(`branch_address_${i}`, branch.branch_address || "");
      form.append(`branch_city_${i}`, branch.branch_city || "");
      form.append(`branch_state_${i}`, branch.branch_state || "");
      form.append(`branch_zipcode_${i}`, branch.branch_zipcode || "");
      form.append(`branch_country_${i}`, branch.branch_country || "");
      form.append(`gst_number_${i}`, branch.gst_number || "");
    });

    const response = await axios.put(
      `${API_BASE_PATH}/company/update/${contact_id}`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

// ✅ DELETE company by contact_id
export const deleteCompany = async (contact_id: string): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_PATH}/company/${contact_id}`, {
      headers: getAuthHeaders(),
    });
  } catch (error) {
    throw error;
  }
};

export const deleteCompaniesBulk = async (contactIds: string[]): Promise<void> => {
  try {
    const contactIdsParam = contactIds.join(','); // e.g., "CT2796,CT2797"

    const response = await axios.delete(
      `${API_BASE_PATH}/company-bulk/?contact_ids=${encodeURIComponent(contactIdsParam)}`,
      {
        headers: getAuthHeaders(),
      }
    );

  } catch (error) {
    throw error;
  }
};


// ✅ Export all contacts - PDF
export const exportContactsPDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(`${BASE_URL}/contacts/export/pdf/contacts`, {
      responseType: "blob",
      headers: getAuthHeaders(),
    });

    toast.success("Contacts PDF exported successfully");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details ||
      error?.response?.data?.message ||
      "Failed to export PDF";

    toast.error(errorMessage);
    throw error;
  }
};

// ✅ Export all contacts - Excel
export const exportContactsExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(`${BASE_URL}/contacts/export/excel/contacts`, {
      responseType: "blob",
      headers: getAuthHeaders(),
    });

    toast.success("Contacts Excel exported successfully");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details ||
      error?.response?.data?.message ||
      "Failed to export Excel";

    toast.error(errorMessage);
    throw error;
  }
};

// ✅ Export selected contacts - PDF
export const exportSelectedContactsPDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("contact_ids", id));

    const res = await axios.get(
      `${BASE_URL}/contacts/export/pdf/multiple/selected/contacts?${params.toString()}`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected contacts PDF exported successfully");
    return res.data;
  } catch (error: any) {
  let errorMessage = "Failed to export";

  if (error?.response?.data instanceof Blob) {
    const text = await error.response.data.text();
    try {
      const json = JSON.parse(text);
      errorMessage = json?.details || json?.message || errorMessage;
    } catch {
      // fallback
    }
  } else {
    errorMessage =
      error?.response?.data?.details ||
      error?.response?.data?.message ||
      errorMessage;
  }

  toast.error(errorMessage);
  throw error;
}

};

// ✅ Export selected contacts - Excel
export const exportSelectedContactsExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("contact_ids", id));

    const res = await axios.get(
      `${BASE_URL}/contacts/export/excel/multiple/selected/contacts?${params.toString()}`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected contacts Excel exported successfully");
    return res.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details ||
      error?.response?.data?.message ||
      "Failed to export selected Excel";

    toast.error(errorMessage);
    throw error;
  }
};

// ✅ Import single contact Excel
export const importSingleContactExcel = async (file: File) => {
  try {
    const form = new FormData();
    form.append("file", file);

    const res = await axios.post(
      `${BASE_URL}/contacts/import/excel/single/data`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Single contact imported");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to import contact");
    throw error;
  }
};

// ✅ Import multiple contacts Excel
export const importMultipleContactsExcel = async (file: File) => {
  try {
    const form = new FormData();
    form.append("file", file);

    const res = await axios.post(
      `${BASE_URL}/import/excel/multiple/data`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Multiple contacts imported");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to import contacts");
    throw error;
  }
};

export const checkDuplicateContacts = async (
  file: File,
  referenceName: string
) => {
  try {
    const form = new FormData();
    form.append("file", file);
    form.append("reference_name", referenceName);

    const res = await axios.post(
      `${BASE_URL}/contacts/import/check-duplicates`,
      form,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Duplicate check complete");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to check duplicates");
    throw error;
  }
};

// ✅ Get metadata of duplicate check
export const getDuplicateCheckMetadata = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/contacts/check/metadata`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch metadata");
    throw error;
  }
};

// ✅ Import non-duplicate contacts
export const importNonDuplicateContacts = async (referenceName: string) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/contacts/import?reference_name=${encodeURIComponent(
        referenceName
      )}`,
      {}, // POST body (empty object)
      {
        headers: getAuthHeaders(),
      }
    );

    toast.success("Non-duplicate contacts imported");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to import non-duplicates");
    throw error;
  }
};

// ✅ Fetch import summary
export const fetchImportSummary = async (): Promise<ImportSummary> => {
  try {
    const res = await axios.get(`${BASE_URL}/contacts/import/summary`, {
      headers: getAuthHeaders(),
    });

    toast.success("Import summary fetched");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to fetch import summary");
    throw error;
  }
};

// ✅ Update lead owner for a contact
export const updateLeadOwner = async (
  contactId: string,
  users: string
): Promise<string> => {
  try {
    const payload = new URLSearchParams();
    payload.append("users", users);

    const response = await axios.put<string>(
      `${BASE_URL}/contacts/${contactId}/update-leadowner`,
      payload,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    //toast.success("✅ Lead owner updated successfully");
    return response.data;
  } catch (error: any) {
    showAxiosError(error, "❌ Failed to update lead owner");
    throw error;
  }
};
// ✅ Dynamically update lead owner for multiple contacts (bulk)
export const updateBulkLeadOwner = async (
  contactIds: string[], // dynamic array of contact IDs
  users: string // dynamic lead owner ID
): Promise<any> => {
  try {
    if (!contactIds.length || !users) {
      throw new Error("Missing contact IDs or lead owner ID");
    }

    const payload = new URLSearchParams();
    payload.append("contact_ids", contactIds.join(",")); // convert array to comma-separated
    payload.append("users", users); // new owner ID

    const res = await axios.put(
      `${BASE_URL}/contacts/update-leadowner/bulk`,
      payload,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    //toast.success("Lead owner updated successfully for selected contacts");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to update lead owner in bulk");
    throw error;
  }
};

// ✅ Get companies by user ID
export const fetchCompaniesByUserId = async (userId: string): Promise<CompanyResponse[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/companies/by-user/${userId}`, {
      headers: getAuthHeaders(),
    });

    const data = response.data;

    let companies: CompanyResponse[] = [];
    if (Array.isArray(data)) {
      companies = data;
    } else if (data && Array.isArray(data.companies)) {
      companies = data.companies;
    } else if (data && Array.isArray(data.data)) {
      companies = data.data;
    } else if (data && Array.isArray(data.result)) {
      companies = data.result;
    } else if (data && Array.isArray(data.items)) {
      companies = data.items;
    } else {
      console.error('Unexpected response structure:', data);
      throw new Error("API response 'companies' is not an array");
    }

    return companies;
  } catch (error: any) {
    console.error('Error in fetchCompaniesByUserId:', error);
    showAxiosError?.(error, `Failed to load contacts for user ${userId}`);
    throw error; 
  }
};

// ✅ Export Contacts as Customized PDF
export const exportContactsPdfCustomized = async (
  fields: string,
  contact_ids: string[]
): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("fields", fields);
    contact_ids.forEach((id) => queryParams.append("contact_ids", id));

    const response = await axios.get(
      `${BASE_URL}/export/contacts/pdf/customized?${queryParams.toString()}`,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        responseType: "blob", // required for file download
      }
    );

    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to export customized PDF");
    throw error;
  }
};

// ✅ Export Contacts as Customized Excel
export const exportContactsExcelCustomized = async (
  fields: string,
  contact_ids: string[]
): Promise<Blob> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("fields", fields);
    contact_ids.forEach((id) => queryParams.append("contact_ids", id));

    const response = await axios.get(
      `${BASE_URL}/export/contacts/excel/customized?${queryParams.toString()}`,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        responseType: "blob", // required for .xlsx file
      }
    );

    return response.data;
  } catch (error) {
    showAxiosError(error, "Failed to export customized Excel");
    throw error;
  }
};

export interface BasicUser {
  user_id: string;
  name: string;
  email: string;
  phone_no: string;
  role: string;
  status: boolean;
  client_id: string;
}

export const fetchUser = async (
  userId: string,
  token: string
): Promise<BasicUser> => {
  const response = await axios.get(`${BASE_URL}/user/${userId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ✅ Create Company Outsource
export const createCompanyOutsource = async (payload: {
  company_name: string;
  company_mail?: string;
  company_phone: string;
  contact_remark?: string;
  client_id: string;
}): Promise<string> => {
  try {
    const formData = new URLSearchParams();
    formData.append("company_name", payload.company_name);
    formData.append("company_phone", payload.company_phone);
    formData.append("client_id", payload.client_id);
    formData.append("company_mail", payload.company_mail || "");
    formData.append("contact_remark", payload.contact_remark || "");

    const res = await axios.post(`${BASE_URL}/company/outsource`, formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to create company outsource");
    throw error;
  }
};

// ✅ Update Company Notes
export const updateCompanyNotes = async (
  contactId: string,
  note: string
): Promise<string> => {
  try {
    const formData = new URLSearchParams();
    formData.append("company_notes", note || "");

    const res = await axios.put(
      `${BASE_URL}/company/notes/${contactId}`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to update company notes");
    throw error;
  }
};

// ✅ Get Company Notes History
export interface CompanyNote {
  note: string;
  modified_at: string;
  modified_by: string;
}

export const getCompanyNotesHistory = async (
  contactId: string
): Promise<CompanyNote[]> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/branch/company/history/notes/${contactId}`,
      {
        headers: getAuthHeaders(),
      }
    );

    return res.data?.company_notes || [];
  } catch (error) {
    showAxiosError(error, "Failed to fetch company notes history");
    throw error;
  }
};
// ✅ Create Company Note
export interface CreateCompanyNotePayload {
  company_name: string;
  company_mail?: string | null;
  company_phone: string;
  contact_remark?: string | null;
  client_id: string;
  company_notes: string;
}

export const createCompanyNote = async (
  contactId: string,
  payload: CreateCompanyNotePayload
): Promise<string> => {
  try {
    const formData = new URLSearchParams();
    formData.append("company_name", payload.company_name);
    formData.append("company_mail", payload.company_mail || "");
    formData.append("company_phone", payload.company_phone);
    formData.append("contact_remark", payload.contact_remark || "");
    formData.append("client_id", payload.client_id);
    formData.append("company_notes", payload.company_notes || "");

    const res = await axios.post(
      `${BASE_URL}/company/notes/${contactId}`,
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          ...getAuthHeaders(),
        },
      }
    );

    toast.success("Note created successfully");
    return res.data;
  } catch (error) {
    showAxiosError(error, "Failed to create company note");
    throw error;
  }
};

// ✅ Types
export type ActionType = "Add_New_Record" | "Update_Existing_Record";
export type LeadSourceType = "SingleLeadSource" | "PerLeadSource";
export type LeadAssignType = "AssignSingleUser" | "AssignPerUser";

// ✅ Error item structure from backend
export interface ImportError {
  row: number;
  error: string;
  phone?: string;
  email?: string;
}

// ✅ Response
export interface ImportContactsResult {
  message: string;
  created_contacts: number;
  updated_contacts: number;
  skipped_duplicates: number;
  skipped_invalid: number;
  created_leads: number;
  created_branches: number;
  errors: ImportError[];
}

// ✅ Input options
export interface ImportContactsOptions {
  file: File; // Excel file
  Action_type: ActionType;

  // Duplicates handling
  Allow_duplicate?: "Yes" | "No";
  Check_Base?: string | null;

  // Country code
  countrycode: "Yes" | "No";
  countrycodevalue?: string | null;

  // Lead Source
  leadsource_type?: LeadSourceType;
  leadsource_id?: string | null;
  lead_source_column_name?: string | null;
  leadsource_mapping?: Record<string, string> | string | null;

  // Lead Assign
  leadAssign?: LeadAssignType;
  lead_id_single?: string | null;
  lead_id_column_name?: string | null;
  lead_id_mapping?: Record<string, string> | string | null;

  // Field Mapping
  field_mapping: Record<string, string>;

  [key: string]: any;
}

/**
 * ✅ importContacts — Dynamic Excel import for Leads/Contacts
 * Supports Add / Update mode, mapping JSONs, duplicate checks, etc.
 */
export const importContacts = async (
  opts: ImportContactsOptions
): Promise<ImportContactsResult | null> => {
  try {
    const form = new FormData();

    // Required
    form.append("file", opts.file);
    form.append("Action_type", opts.Action_type);
    form.append("countrycode", opts.countrycode);
    form.append("field_mapping", JSON.stringify(opts.field_mapping));

    // Optional handling (avoid sending undefined)
    if (opts.Allow_duplicate !== undefined)
      form.append("Allow_duplicate", opts.Allow_duplicate);
    if (opts.Check_Base)
      form.append("Check_Base", String(opts.Check_Base));
    if (opts.countrycodevalue)
      form.append("countrycodevalue", String(opts.countrycodevalue));

    // Lead Source
    if (opts.leadsource_type)
      form.append("leadsource_type", opts.leadsource_type);
    if (opts.leadsource_id)
      form.append("leadsource_id", String(opts.leadsource_id));
    if (opts.lead_source_column_name)
      form.append("lead_source_column_name", String(opts.lead_source_column_name));
    if (opts.leadsource_mapping) {
      const mapping =
        typeof opts.leadsource_mapping === "string"
          ? opts.leadsource_mapping
          : JSON.stringify(opts.leadsource_mapping);
      form.append("leadsource_mapping", mapping);
    }

    // Lead Assign
    if (opts.leadAssign)
      form.append("leadAssign", opts.leadAssign);
    if (opts.lead_id_single)
      form.append("lead_id_single", String(opts.lead_id_single));
    if (opts.lead_id_column_name)
      form.append("lead_id_column_name", String(opts.lead_id_column_name));
    if (opts.lead_id_mapping) {
      const mapping =
        typeof opts.lead_id_mapping === "string"
          ? opts.lead_id_mapping
          : JSON.stringify(opts.lead_id_mapping);
      form.append("lead_id_mapping", mapping);
    }

    // ✅ POST request
    const res = await axios.post(`${API_BASE_PATH}/mapping-field_new`, form, {
      headers: {
        ...getAuthHeaders(),
      },
    });

    const data = res.data as ImportContactsResult;

    // ✅ Toast summary
    const created = data.created_contacts || 0;
    const updated = data.updated_contacts || 0;
    const skipped = data.skipped_invalid || 0;

    if (created || updated) {
      toast.success(
        `Import finished: ${created} created, ${updated} updated, ${skipped} skipped.`
      );
    } else if (data.errors?.length) {
      toast.warn(`Import completed with ${data.errors.length} errors.`);
    } else {
      toast.info(data.message || "Import completed.");
    }

    return data;
  } catch (error) {
    showAxiosError(error, "Failed to import contacts");
    return null;
  }
};